import React, { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LANGUAGES,
  getLanguageFromPath,
  getPageIdFromPath,
  getLocalizedPath,
} from '../i18n/routes'

function Flag({ code, className = 'w-5 h-3.5' }) {
  if (code === 'da') {
    return (
      <svg
        viewBox="0 0 37 28"
        aria-hidden="true"
        className={`shrink-0 rounded-[2px] shadow-[0_0_1px_rgba(0,0,0,0.3)] ${className}`}
      >
        <rect width="37" height="28" fill="#C8102E" />
        <rect x="12" width="4" height="28" fill="#FFFFFF" />
        <rect y="12" width="37" height="4" fill="#FFFFFF" />
      </svg>
    )
  }
  if (code === 'de') {
    return (
      <svg
        viewBox="0 0 5 3"
        aria-hidden="true"
        className={`shrink-0 rounded-[2px] shadow-[0_0_1px_rgba(0,0,0,0.3)] ${className}`}
      >
        <rect width="5" height="1" y="0" fill="#000000" />
        <rect width="5" height="1" y="1" fill="#DD0000" />
        <rect width="5" height="1" y="2" fill="#FFCE00" />
      </svg>
    )
  }
  if (code === 'en') {
    return (
      <svg
        viewBox="0 0 60 30"
        aria-hidden="true"
        className={`shrink-0 rounded-[2px] shadow-[0_0_1px_rgba(0,0,0,0.3)] ${className}`}
      >
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
        <path
          d="M0,0 L25,12.5 M60,30 L35,17.5 M60,0 L35,12.5 M0,30 L25,17.5"
          stroke="#C8102E"
          strokeWidth="2"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#FFFFFF" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </svg>
    )
  }
  return null
}

export default function LanguageSwitcher({ isMobile = false, onCloseMobile }) {
  const location = useLocation()
  const dropdownRef = useRef(null)

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) dropdownRef.current?.removeAttribute('open')
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && dropdownRef.current?.open) {
        dropdownRef.current.open = false
        dropdownRef.current.querySelector('summary')?.focus()
      }
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  const currentLang = getLanguageFromPath(location.pathname)
  const currentPageId = getPageIdFromPath(location.pathname)
  const labels = {
    da: { nav: 'Vælg sprog', switchTo: 'Skift til' },
    en: { nav: 'Choose language', switchTo: 'Switch to' },
    de: { nav: 'Sprache wählen', switchTo: 'Wechseln zu' },
  }[currentLang]

  if (isMobile) {
    return (
      <div className="pt-4 border-t border-[#C68D94]/20">
        <span className="block text-[11px] font-semibold tracking-wider text-[#3E3435]/60 mb-2">
          {currentLang === 'de'
            ? 'Sprache'
            : currentLang === 'en'
            ? 'Language'
            : 'Sprog'}
        </span>
        <div className="grid grid-cols-3 gap-2">
          {Object.values(LANGUAGES).map((lang) => {
            const targetUrl = currentPageId
              ? getLocalizedPath(currentPageId, lang.code)
              : lang.code === 'da'
              ? '/'
              : `/${lang.code}`
            const isActive = currentLang === lang.code

            return (
              <Link
                key={lang.code}
                to={targetUrl}
                onClick={onCloseMobile}
                className={`flex items-center justify-center py-2.5 px-3 rounded-xl border transition-all ${
                  isActive
                    ? 'bg-[#F7ECEE] border-[#A35D67] shadow-xs'
                    : 'bg-white border-[#C68D94]/30 hover:bg-[#FDF8F7] hover:border-[#A35D67]'
                }`}
                title={lang.name}
                aria-label={`${labels.switchTo} ${lang.name}`}
                aria-current={isActive ? 'true' : undefined}
              >
                <Flag code={lang.code} className="w-7 h-4.5" />
              </Link>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <nav
      className="relative flex items-center min-[667px]:gap-1 min-[667px]:p-1 rounded-full border border-[#C68D94]/40 bg-white/70 hover:bg-white transition-all shadow-2xs"
      aria-label={labels.nav}
    >
      <details ref={dropdownRef} className="relative min-[667px]:hidden">
        <summary className="flex items-center justify-center gap-1.5 px-2.5 py-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden" aria-label={labels.nav}>
          <Flag code={currentLang} className="w-5 h-3.5" />
          <i className="fa-regular fa-chevron-down text-[9px] text-[#A35D67]" aria-hidden="true" />
        </summary>
        <div className="absolute right-0 top-full mt-2 flex flex-col gap-1 rounded-xl border border-[#C68D94]/40 bg-white p-1.5 shadow-lg z-50">
          {Object.values(LANGUAGES).map((lang) => {
            const targetUrl = currentPageId
              ? getLocalizedPath(currentPageId, lang.code)
              : lang.code === 'da'
              ? '/'
              : `/${lang.code}`
            return (
              <Link
                key={lang.code}
                to={targetUrl}
                onClick={() => { dropdownRef.current.open = false }}
                className={`flex items-center justify-center rounded-lg p-2 hover:bg-[#FDF8F7] ${currentLang === lang.code ? 'bg-[#F7ECEE]' : ''}`}
                title={lang.name}
                aria-label={`${labels.switchTo} ${lang.name}`}
                aria-current={currentLang === lang.code ? 'true' : undefined}
              >
                <Flag code={lang.code} className="w-6 h-4" />
              </Link>
            )
          })}
        </div>
      </details>
      {Object.values(LANGUAGES).map((lang) => {
        const targetUrl = currentPageId
          ? getLocalizedPath(currentPageId, lang.code)
          : lang.code === 'da'
          ? '/'
          : `/${lang.code}`
        const isActive = currentLang === lang.code

        return (
          <Link
            key={lang.code}
            to={targetUrl}
            className={`hidden min-[667px]:flex items-center min-[667px]:justify-center p-1 rounded-full transition-all duration-150 ${
              isActive
                ? 'bg-[#F7ECEE] ring-1.5 ring-[#A35D67] opacity-100 scale-105'
                : 'opacity-65 hover:opacity-100 hover:bg-[#FDF8F7]'
            }`}
            title={lang.name}
            aria-label={`${labels.switchTo} ${lang.name}`}
            aria-current={isActive ? 'true' : undefined}
          >
            <Flag code={lang.code} className="w-5 h-3.5" />
          </Link>
        )
      })}
    </nav>
  )
}
